import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  CompareState,
  decodeCompareState,
  encodeCompareState,
} from "./compareState";

// A change of the state: the state after it, from the state before it. The
// helpers of compareState (addProduct, removeProduct, setReference) are of
// this shape once their id is given.
export type CompareChange = (state: CompareState) => CompareState;

/**
 * The compare page's state, read from the address, and a function that
 * changes it.
 *
 * Every change is a new entry of the browser's history, so the back button
 * steps through the states. The parameters the state does not own (the
 * detail overlay's) are kept.
 */
export function useCompareState(): [
  CompareState,
  (change: CompareChange) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => decodeCompareState(searchParams), [searchParams]);

  const change = useCallback(
    (next: CompareChange) => {
      setSearchParams((current) =>
        encodeCompareState(next(decodeCompareState(current)), current),
      );
    },
    [setSearchParams],
  );

  return [state, change];
}
